import { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import Select, { SingleValue, components } from 'react-select';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import Button from 'common/button/button';
import { DATE_Of_MONTH } from 'constants/CONSTATNS';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import useStoreStore from 'zustands/useStoreStore';
import { selectDateOfMonthArr } from 'constants/dataArr';
import MonthInput from 'common/month-input/month-input';
import { IPaystubEmployeeItem } from '~/types/paystub';

const PaystubEmployeeList = () => {
  const navigate = useNavigate();
  const calDayArr = selectDateOfMonthArr();

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      cellStyle: {
        textAlign: 'center',
      },
    }),
    [],
  );

  const colorStyles = {
    control: (styles: any) => ({
      ...styles,
      backgroundColor: 'none',
      border: 'none',
      width: '100%',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
    }),
    valueContainer: (styles: any) => ({
      ...styles,
      display: 'flex',
      alignItems: 'center',
      padding: '0',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  const { setLoadingState } = useLoadingStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [calDay, setCalDay] = useState<SingleValue<DATE_Of_MONTH>>({ value: '말일', label: '말일' }); // 급여정산일
  const [workingEmpList, setWorkingEmpList] = useState<IPaystubEmployeeItem[]>([]); // 근무 직원
  const [endEmpList, setEndEmpList] = useState<IPaystubEmployeeItem[]>([]); // 퇴사 직원
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  // 근무중 직원 컬럼 정의
  const workingEmpColumns: (ColDef | ColGroupDef)[] = useMemo(
    () => [
      {
        field: 'NUMBER',
        headerName: 'NO.',
        flex: 0.5,
        valueGetter: (params) => (params.node?.rowIndex != null ? params.node.rowIndex + 1 : ''),
      },
      {
        field: 'EMP_NAME',
        headerName: '이름',
        flex: 1,
      },
      {
        field: 'BIRTHDATE',
        headerName: '생년월일',
        flex: 1,
        valueFormatter: ({ value }) => (value ? moment(value).format('YYMMDD') : ''),
      },
      {
        field: 'MobileNo',
        headerName: '핸드폰 번호',
        flex: 1.5,
      },
      {
        field: 'START',
        headerName: '입사일',
        flex: 1,
      },
      {
        field: 'INSURANCE_TYPE',
        headerName: '세금유형',
        flex: 1,
      },
    ],
    [calDay],
  );

  // 퇴사처리 직원 컬럼 정의
  const endEmpColumns: (ColDef | ColGroupDef)[] = useMemo(
    () => [
      {
        field: 'NUMBER',
        headerName: 'no.',
        flex: 0.5,
        valueGetter: (params) => (params.node?.rowIndex != null ? params.node.rowIndex + 1 : ''),
      },
      {
        field: 'EMP_NAME',
        headerName: '이름',
        flex: 1,
      },
      {
        field: 'BIRTHDATE',
        headerName: '생년월일',
        flex: 1,
        valueFormatter: ({ value }) => (value ? moment(value).format('YYMMDD') : ''),
      },
      {
        field: 'MobileNo',
        headerName: '핸드폰 번호',
        flex: 1.5,
      },
      {
        field: 'START',
        headerName: '입사일',
        flex: 1,
      },
      {
        field: 'END',
        headerName: '퇴사일',
        flex: 1,
      },
    ],
    [],
  );

  // 급여정산일 변경
  const handleDateChange = (date: Date) => {
    setCurrentMonth(date);
  };

  // 급여정산일 선택
  const handleSelectCalDay = useCallback(
    (data: SingleValue<DATE_Of_MONTH>) => {
      updatePaystubCalculationDate(data?.value || '1');
    },
    [calDay],
  );

  // 직원 로우 클릭
  const handleRowClicked = (empSeq: number) => {
    navigate('/view-information', {
      state: {
        empData: {
          EMP_SEQ: empSeq,
        },
      },
    });
  };

  // 작성 및 전송 페이지 이동
  const enterPayStatementScreen = () => {
    navigate('/paystub-send-management');
  };

  // 급여정산일 조회(API)
  const fetchPaystubCalculationDate = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getPaystubCalculationDate`, {
        // STORE_SEQ: storeSeqNumber.toString(),
      });
      if (data.message === 'SUCCESS') {
        setCalDay({
          value: data.resultData.CALCULATE_DAY.toString(),
          label:
            data.resultData.CALCULATE_DAY === '1'
              ? '말일'
              : (Number(data.resultData.CALCULATE_DAY) - 1).toString() + '일',
        });
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/getPaystubCalculationDate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 직원목록 조회(API)
  const fetchPaystubEmployeeList = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/paystubEmpList`, {
        YEAR: moment(currentMonth).year().toString(),
        MONTH: (moment(currentMonth).month() + 1).toString(),
        // STORE_SEQ: storeSeqNumber.toString(),
        ORDER_TYPE: 'START',
        SORT_TYPE: 'ASC',
      });

      if (data.message === 'SUCCESS') {
        const { empList } = data.resultData;

        if (empList.length > 0) {
          setWorkingEmpList(
            empList
              .filter((i: IPaystubEmployeeItem) => i.end === false)
              .map((i: IPaystubEmployeeItem, index: number) => {
                return {
                  ...i,
                  NUMBER: index + 1,
                };
              }),
          );
          setEndEmpList(
            empList
              .filter((i: IPaystubEmployeeItem) => i.end === true)
              .map((i: IPaystubEmployeeItem, index: number) => {
                return {
                  ...i,
                  NUMBER: index + 1,
                };
              }),
          );
        }
      } else {
        throw new Error('/auth/getPaystubMember');
      }
    } catch (err) {
      checkErrorLog(err, 'employee-list-for-payroll /auth/getPaystubMember');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여정산일 업데이트(API)
  const updatePaystubCalculationDate = async (calDay: string) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/updatePaystubCalculationDate`, {
        // STORE_SEQ: storeSeqNumber,
        // 말일이면 "1" 나머지는 1일 이면 +1 해서 "2" 로 변환
        CALCULATE_DAY: calDay === '말일' ? '1' : (Number(calDay) + 1).toString(),
      });

      if (data.message === 'SUCCESS') {
        fetchPaystubEmployeeList();
        fetchPaystubCalculationDate();
      } else {
        throw new Error('/paystub/updatePaystubCalculationDate');
      }
    } catch (err) {
      checkErrorLog(err, 'paystub-employee-list /paystub/updatePaystubCalculationDate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  useEffect(() => {
    fetchPaystubEmployeeList();
  }, [currentMonth]);

  useEffect(() => {
    fetchPaystubCalculationDate();
  }, []);

  return (
    <>
      <WithSideBarAndNavBar>
        <div className="flex flex-col">
          <div className="">
            <div>
              <div className="flex">
                <div className="font-medium text-[#868686]">
                  급여명세서 <span className="px-2.5 text-sm font-normal leading-5 text-[#c1c1c1]">{'>'}</span> 직원목록
                </div>
              </div>
              <div className="text-[28px] font-bold leading-[38px] text-[#333333]">급여명세서 직원목록</div>

              <div className="mt-5">
                <div className="text-xs font-bold leading-[18px] text-[#505054]">
                  급여명세서를 보낼 직원을 등록하고 관리해 보세요.
                </div>
                <div className="text-xs font-normal leading-[18px] text-[#999999]">
                  - 급여명세서 목록은 직원 목록과 연동돼요.
                </div>
                <div className="text-xs font-normal leading-[18px] text-[#999999]">
                  - 직원정보 수정은 직원목록에서 수정할 수 있어요.
                </div>
                <div className="text-xs font-normal leading-[18px] text-[#999999]">
                  - 오른쪽 ‘급여작성 및 전송하기’를 클릭하면 급여명에서를 발송할 수 있어요.
                </div>
              </div>
              <div className="mt-5 flex  items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <MonthInput onChange={handleDateChange} typeText value={currentMonth} />

                  <Select
                    className="min-h-10 w-[180px] rounded-lg border border-[#e9e9e9] bg-white text-sm"
                    components={{
                      IndicatorSeparator: () => null,
                      DropdownIndicator: () => (
                        <div style={{ paddingRight: '15px' }}>
                          <ArrowDownIcon />
                        </div>
                      ),
                      Control: ({ children, ...props }: any) => {
                        return (
                          <components.Control {...props}>
                            <div className="pl-2.5 text-base font-medium text-[#868686]">급여정산일</div>
                            {children}
                          </components.Control>
                        );
                      },
                    }}
                    isSearchable={false}
                    value={calDay}
                    styles={colorStyles}
                    options={calDayArr}
                    onChange={handleSelectCalDay}
                  />
                </div>
                <div className="w-[190px]">
                  <Button label="급여작성 및 전송하기" type="type2" height={40} action={enterPayStatementScreen} />
                </div>
              </div>
            </div>
            <div className="-mb-2.5 ml-auto"></div>
          </div>

          <div>
            <div className="mt-[18px] rounded-2xl bg-white p-[2%]">
              <div className="flex pb-2.5 pt-2.5">
                <div className="text-[22px] font-bold leading-8 text-[#333333] mr-1">근무중 직원</div>
                <div className="pl-0.5 text-[22px] font-bold leading-8 text-[#49ae9e]">{workingEmpList.length}</div>
              </div>
              <div className="ag-theme-quartz" style={{ height: '300px', width: '100%' }}>
                <AgGridReact
                  rowData={workingEmpList}
                  columnDefs={workingEmpColumns}
                  defaultColDef={defaultColDef}
                  onRowClicked={(event) => handleRowClicked(event.data.EMP_SEQ)}
                  pagination={true}
                  paginationPageSize={20}
                  enableRangeSelection={false}
                  suppressExcelExport={false}
                  enableCharts={true}
                  autoSizeStrategy={{ type: 'fitGridWidth' }}
                  overlayNoRowsTemplate={
                    '<span aria-live="polite" aria-atomic="true" style="padding: 10px;">근무중 직원 없음</span>'
                  }
                />
              </div>
            </div>

            <div className="mt-6 rounded-2xl bg-white p-[2%]">
              <div className="flex pb-2.5 pt-2.5">
                <div className="text-[22px] font-bold leading-8 text-[#333333] mr-1">퇴사처리 직원</div>
                <div className="pl-0.5 text-[22px] font-bold leading-8 text-[#e47c77]">{endEmpList.length}</div>
              </div>
              <div className="ag-theme-quartz" style={{ height: '300px', width: '100%' }}>
                <AgGridReact
                  rowData={endEmpList}
                  columnDefs={endEmpColumns}
                  defaultColDef={defaultColDef}
                  pagination={true}
                  paginationPageSize={20}
                  enableRangeSelection={false}
                  suppressExcelExport={false}
                  onRowClicked={(event) => handleRowClicked(event.data.EMP_SEQ)}
                  enableCharts={true}
                  autoSizeStrategy={{ type: 'fitGridWidth' }}
                  overlayNoRowsTemplate={
                    '<span aria-live="polite" aria-atomic="true" style="padding: 10px;">퇴사자 없음</span>'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </WithSideBarAndNavBar>
    </>
  );
};

export default PaystubEmployeeList;
